import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";

const SECTIONS = [
    { id: "introducao", title: "1. Introdução" },
    { id: "quem-somos", title: "2. Quem somos" },
    { id: "dados-coletados", title: "3. Dados que coletamos" },
    { id: "finalidades", title: "4. Como usamos seus dados" },
    { id: "base-legal", title: "5. Base legal (LGPD)" },
    { id: "permissoes", title: "6. Permissões do aplicativo" },
    { id: "compartilhamento", title: "7. Compartilhamento com terceiros" },
    { id: "terceiros", title: "8. Serviços de terceiros" },
    { id: "armazenamento", title: "9. Armazenamento e segurança" },
    { id: "retencao", title: "10. Retenção de dados" },
    { id: "direitos", title: "11. Seus direitos" },
    { id: "criancas", title: "12. Privacidade de crianças" },
    { id: "transferencia", title: "13. Transferência internacional" },
    { id: "alteracoes", title: "14. Alterações nesta política" },
    { id: "contato", title: "15. Contato e Encarregado (DPO)" },
];

export default function PrivacyPolicy() {
    useEffect(() => {
        document.title = "Política de Privacidade — Dentro";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div
            className="min-h-screen bg-white text-[#0B1C3A] overflow-x-hidden"
            data-testid="privacy-root"
        >
            <Header />

            <main className="pt-28 lg:pt-36 pb-20">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <Link
                        to="/"
                        data-testid="privacy-back-link"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1C3A]/70 hover:text-[#16A34A] transition-colors"
                    >
                        <ArrowLeft size={16} /> Voltar para o início
                    </Link>

                    <div className="mt-6">
                        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#16A34A]">
                            Dentro · Legal
                        </span>
                        <h1
                            data-testid="privacy-title"
                            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
                        >
                            Política de Privacidade
                        </h1>
                        <p
                            data-testid="privacy-updated"
                            className="mt-4 text-sm text-[#475569]"
                        >
                            Última atualização: 19 de abril de 2026 · Versão 1.0
                        </p>
                        <p className="mt-6 text-lg text-[#475569] leading-relaxed">
                            Esta Política descreve como o aplicativo e o site
                            Dentro (<em>Dentro.app.br</em>) coletam, usam,
                            armazenam e protegem os seus dados pessoais. Leia
                            com atenção antes de usar o serviço.
                        </p>
                    </div>

                    {/* Índice */}
                    <nav
                        data-testid="privacy-toc"
                        className="mt-10 rounded-3xl bg-[#F8FAFC] border border-slate-100 p-6 lg:p-8"
                    >
                        <h2 className="font-display text-lg font-bold text-[#0B1C3A]">
                            Índice
                        </h2>
                        <ol className="mt-4 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                            {SECTIONS.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-[#0B1C3A]/80 hover:text-[#16A34A] font-semibold"
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    <article className="mt-12 prose-custom space-y-12">
                        <Section id="introducao" title="1. Introdução">
                            <p>
                                Bem-vindo(a) ao Dentro. Nossa missão é ajudar
                                você a entender rótulos de produtos alimentícios
                                e tomar decisões alinhadas à sua saúde, às suas
                                restrições e aos seus objetivos. Para isso,
                                tratamos alguns dados pessoais — sempre com
                                transparência e em conformidade com a{" "}
                                <strong>
                                    Lei Geral de Proteção de Dados (LGPD — Lei
                                    nº 13.709/2018)
                                </strong>
                                , com as diretrizes da{" "}
                                <strong>App Store (Apple)</strong> e com a{" "}
                                <strong>Google Play Data Safety</strong>.
                            </p>
                        </Section>

                        <Section id="quem-somos" title="2. Quem somos">
                            <p>
                                O Dentro é operado por{" "}
                                <strong>Dentro Tecnologia Ltda.</strong>{" "}
                                ("Dentro", "nós"), pessoa jurídica de direito
                                privado, inscrita no CNPJ sob o nº a ser
                                informado, com sede no Brasil. Atuamos como{" "}
                                <strong>Controlador</strong> dos dados pessoais
                                tratados nesta política.
                            </p>
                        </Section>

                        <Section
                            id="dados-coletados"
                            title="3. Dados que coletamos"
                        >
                            <p>
                                Coletamos somente o necessário para oferecer o
                                serviço. As categorias abaixo correspondem aos{" "}
                                <em>data types</em> exigidos pela App Store e
                                pela Google Play:
                            </p>

                            <h3 className="font-display text-lg font-bold mt-6">
                                3.1 Dados fornecidos por você
                            </h3>
                            <ul>
                                <li>
                                    <strong>Conta:</strong> nome, e-mail e,
                                    opcionalmente, foto de perfil.
                                </li>
                                <li>
                                    <strong>Perfil de saúde ("Meu Filtro"):</strong>{" "}
                                    alergias, intolerâncias, restrições
                                    alimentares (ex.: glúten, lactose,
                                    amendoim), condições como diabetes ou
                                    hipertensão e preferências (vegano,
                                    vegetariano).
                                </li>
                                <li>
                                    <strong>Objetivos ("Minha Meta"):</strong>{" "}
                                    emagrecer, ganhar massa, mais energia ou
                                    cuidar da saúde.
                                </li>
                                <li>
                                    <strong>Conteúdo de comunicação:</strong>{" "}
                                    mensagens que você envia ao nosso suporte.
                                </li>
                            </ul>

                            <h3 className="font-display text-lg font-bold mt-6">
                                3.2 Dados gerados pelo uso do app
                            </h3>
                            <ul>
                                <li>
                                    <strong>Imagens de câmera:</strong> fotos
                                    tiradas por você de códigos de barras e
                                    embalagens de produtos. As imagens são
                                    processadas para análise e{" "}
                                    <strong>
                                        não são armazenadas de forma permanente
                                    </strong>
                                    , salvo quando você salva um scan no
                                    histórico.
                                </li>
                                <li>
                                    <strong>Histórico de scans:</strong>{" "}
                                    produtos escaneados, data/hora e vereditos
                                    gerados pelo app.
                                </li>
                                <li>
                                    <strong>Identificadores do dispositivo:</strong>{" "}
                                    ID anônimo de instalação, modelo,
                                    versão do sistema operacional e idioma.
                                </li>
                                <li>
                                    <strong>Dados de uso e diagnóstico:</strong>{" "}
                                    eventos de interação (ex.: telas abertas,
                                    cliques em CTA), métricas de performance e
                                    relatórios de erro.
                                </li>
                                <li>
                                    <strong>Dados aproximados de localização:</strong>{" "}
                                    apenas quando você permite, para sugerir
                                    ofertas e alternativas em mercados
                                    próximos.
                                </li>
                            </ul>

                            <h3 className="font-display text-lg font-bold mt-6">
                                3.3 O que NÃO coletamos
                            </h3>
                            <ul>
                                <li>
                                    Não acessamos sua lista de contatos, SMS ou
                                    ligações.
                                </li>
                                <li>
                                    Não vendemos seus dados pessoais para
                                    terceiros.
                                </li>
                                <li>
                                    Não coletamos dados biométricos faciais.
                                </li>
                                <li>
                                    Não coletamos localização precisa em segundo
                                    plano.
                                </li>
                            </ul>
                        </Section>

                        <Section
                            id="finalidades"
                            title="4. Como usamos seus dados"
                        >
                            <ul>
                                <li>
                                    <strong>Funcionalidade principal:</strong>{" "}
                                    analisar ingredientes, nutrientes e
                                    alergênicos e devolver um veredito
                                    (🟢 boa escolha / 🟡 com moderação /
                                    🔴 melhor evitar).
                                </li>
                                <li>
                                    <strong>Personalização:</strong> adaptar o
                                    resultado ao seu "Meu Filtro" e à sua
                                    "Minha Meta".
                                </li>
                                <li>
                                    <strong>Autenticação e conta:</strong>{" "}
                                    criar, manter e proteger a sua conta.
                                </li>
                                <li>
                                    <strong>Suporte ao usuário:</strong>{" "}
                                    responder dúvidas e resolver incidentes.
                                </li>
                                <li>
                                    <strong>Melhoria do produto:</strong>{" "}
                                    entender como o app é usado para corrigir
                                    bugs e desenvolver novas funcionalidades.
                                </li>
                                <li>
                                    <strong>Comunicação:</strong> envio de
                                    avisos operacionais, de segurança e, se
                                    você optar por receber, novidades sobre o
                                    produto.
                                </li>
                                <li>
                                    <strong>Obrigações legais:</strong>{" "}
                                    cumprimento de normas fiscais, regulatórias
                                    e de requisições de autoridade competente.
                                </li>
                            </ul>
                        </Section>

                        <Section id="base-legal" title="5. Base legal (LGPD)">
                            <p>
                                Tratamos seus dados com base nas seguintes
                                hipóteses do art. 7º e art. 11 da LGPD:
                            </p>
                            <ul>
                                <li>
                                    <strong>Consentimento</strong> — para dados
                                    de saúde (alergias, intolerâncias, condições
                                    como diabetes/hipertensão) e localização.
                                </li>
                                <li>
                                    <strong>Execução de contrato</strong> — para
                                    prestar o serviço que você contratou ao
                                    aceitar os Termos de Uso.
                                </li>
                                <li>
                                    <strong>Legítimo interesse</strong> — para
                                    prevenção de fraudes, segurança e melhoria
                                    do produto.
                                </li>
                                <li>
                                    <strong>Obrigação legal ou regulatória</strong>{" "}
                                    — quando exigido por lei.
                                </li>
                            </ul>
                            <p>
                                Você pode revogar o consentimento a qualquer
                                momento, dentro do app em{" "}
                                <em>Configurações → Privacidade</em> ou
                                escrevendo para{" "}
                                <a href="mailto:privacidade@dentro.app.br">
                                    privacidade@dentro.app.br
                                </a>
                                .
                            </p>
                        </Section>

                        <Section
                            id="permissoes"
                            title="6. Permissões do aplicativo"
                        >
                            <ul>
                                <li>
                                    <strong>Câmera:</strong> obrigatória para
                                    escanear códigos de barras e rótulos.
                                    Nenhuma imagem é enviada sem sua ação.
                                </li>
                                <li>
                                    <strong>Biblioteca de fotos (opcional):</strong>{" "}
                                    permite escanear imagens já salvas no seu
                                    dispositivo.
                                </li>
                                <li>
                                    <strong>Localização aproximada (opcional):</strong>{" "}
                                    usada apenas quando você pede sugestões de
                                    mercados próximos.
                                </li>
                                <li>
                                    <strong>Notificações (opcional):</strong>{" "}
                                    envio de lembretes e avisos relevantes ao
                                    seu perfil.
                                </li>
                            </ul>
                            <p>
                                Todas as permissões são revogáveis nas
                                configurações do seu sistema operacional (iOS
                                ou Android).
                            </p>
                        </Section>

                        <Section
                            id="compartilhamento"
                            title="7. Compartilhamento com terceiros"
                        >
                            <p>
                                Nós <strong>não vendemos</strong> seus dados
                                pessoais. Compartilhamos apenas quando
                                necessário com:
                            </p>
                            <ul>
                                <li>
                                    Provedores de infraestrutura de nuvem
                                    (ex.: AWS).
                                </li>
                                <li>
                                    Serviços de análise e de relatório de
                                    erros.
                                </li>
                                <li>
                                    Autoridades públicas, quando exigido por
                                    lei ou ordem judicial.
                                </li>
                                <li>
                                    Empresas sucessoras em caso de fusão,
                                    aquisição ou reestruturação societária
                                    (com notificação prévia a você).
                                </li>
                            </ul>
                        </Section>

                        <Section
                            id="terceiros"
                            title="8. Serviços de terceiros"
                        >
                            <p>
                                Utilizamos os seguintes fornecedores que
                                processam dados em nosso nome, sob contratos
                                de proteção de dados:
                            </p>
                            <ul>
                                <li>
                                    <strong>Amazon Web Services (AWS)</strong>{" "}
                                    — hospedagem e banco de dados.
                                </li>
                                <li>
                                    <strong>Apple App Store / Google Play</strong>{" "}
                                    — distribuição do app e analytics básicos
                                    de instalação.
                                </li>
                                <li>
                                    <strong>PostHog</strong> — análise anônima
                                    de uso para melhorar o produto.
                                </li>
                                <li>
                                    <strong>Provedor de e-mail transacional</strong>{" "}
                                    — envio de confirmações e comunicados
                                    operacionais.
                                </li>
                            </ul>
                            <p>
                                A lista pode ser atualizada conforme o produto
                                evolui; alterações relevantes serão comunicadas.
                            </p>
                        </Section>

                        <Section
                            id="armazenamento"
                            title="9. Armazenamento e segurança"
                        >
                            <p>
                                Adotamos medidas técnicas e administrativas
                                razoáveis para proteger seus dados, incluindo:
                            </p>
                            <ul>
                                <li>Criptografia em trânsito (TLS 1.2+).</li>
                                <li>Criptografia em repouso em bases sensíveis.</li>
                                <li>
                                    Controle de acesso interno por papéis
                                    (princípio do menor privilégio).
                                </li>
                                <li>Registros de auditoria e monitoramento.</li>
                                <li>Backups periódicos e plano de resposta a incidentes.</li>
                            </ul>
                            <p>
                                Apesar dos esforços, nenhum sistema é 100%
                                seguro. Em caso de incidente relevante, você
                                será notificado conforme a LGPD e boas práticas
                                internacionais.
                            </p>
                        </Section>

                        <Section id="retencao" title="10. Retenção de dados">
                            <ul>
                                <li>
                                    <strong>Conta ativa:</strong> enquanto
                                    você mantiver sua conta conosco.
                                </li>
                                <li>
                                    <strong>Histórico de scans:</strong> pode
                                    ser apagado por você a qualquer momento em{" "}
                                    <em>Configurações → Meus scans</em>.
                                </li>
                                <li>
                                    <strong>Conta excluída:</strong> removemos
                                    ou anonimizamos seus dados em até{" "}
                                    <strong>30 dias</strong>, exceto quando a
                                    retenção for exigida por obrigação legal.
                                </li>
                                <li>
                                    <strong>Logs e dados de segurança:</strong>{" "}
                                    mantidos por até 6 meses.
                                </li>
                            </ul>
                        </Section>

                        <Section id="direitos" title="11. Seus direitos">
                            <p>
                                Nos termos da LGPD, você pode, a qualquer
                                tempo:
                            </p>
                            <ul>
                                <li>Confirmar a existência de tratamento.</li>
                                <li>Acessar seus dados.</li>
                                <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
                                <li>
                                    Solicitar anonimização, bloqueio ou
                                    eliminação de dados desnecessários ou
                                    tratados em desconformidade.
                                </li>
                                <li>Portabilidade dos dados a outro fornecedor.</li>
                                <li>Eliminar dados tratados com base em consentimento.</li>
                                <li>
                                    Obter informações sobre compartilhamentos
                                    com entidades públicas e privadas.
                                </li>
                                <li>Revogar o consentimento.</li>
                                <li>
                                    Se opor a tratamentos realizados com base
                                    em outra hipótese legal, caso haja
                                    descumprimento da LGPD.
                                </li>
                            </ul>
                            <p>
                                Para exercer qualquer direito, envie um e-mail
                                para{" "}
                                <a href="mailto:privacidade@dentro.app.br">
                                    privacidade@dentro.app.br
                                </a>
                                . Respondemos em até 15 dias.
                            </p>
                            <p>
                                Você também pode <strong>excluir sua conta</strong>{" "}
                                diretamente no app em{" "}
                                <em>Configurações → Conta → Excluir minha conta</em>.
                            </p>
                        </Section>

                        <Section
                            id="criancas"
                            title="12. Privacidade de crianças"
                        >
                            <p>
                                O Dentro <strong>não é destinado a crianças
                                menores de 13 anos</strong>. Se você for
                                responsável por uma criança que nos forneceu
                                dados sem autorização, entre em contato em{" "}
                                <a href="mailto:privacidade@dentro.app.br">
                                    privacidade@dentro.app.br
                                </a>{" "}
                                que excluiremos as informações.
                            </p>
                        </Section>

                        <Section
                            id="transferencia"
                            title="13. Transferência internacional"
                        >
                            <p>
                                Alguns de nossos fornecedores armazenam dados
                                fora do Brasil (ex.: Estados Unidos). Nesses
                                casos, exigimos garantias contratuais adequadas
                                (cláusulas-padrão, certificações e políticas
                                compatíveis com a LGPD).
                            </p>
                        </Section>

                        <Section
                            id="alteracoes"
                            title="14. Alterações nesta política"
                        >
                            <p>
                                Podemos atualizar esta Política para refletir
                                mudanças legais ou em nosso serviço. Quando a
                                alteração for relevante, notificaremos você
                                pelo app ou por e-mail antes que entre em
                                vigor.
                            </p>
                        </Section>

                        <Section
                            id="contato"
                            title="15. Contato e Encarregado (DPO)"
                        >
                            <p>
                                Dúvidas, solicitações ou reclamações sobre
                                seus dados pessoais devem ser enviadas ao
                                nosso Encarregado de Proteção de Dados:
                            </p>
                            <div
                                data-testid="privacy-contact-card"
                                className="mt-5 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5 flex items-start gap-4"
                            >
                                <div className="h-11 w-11 rounded-xl bg-[#16A34A]/10 grid place-items-center text-[#16A34A] shrink-0">
                                    <Mail size={20} />
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-[#0B1C3A]">
                                        Encarregado de Proteção de Dados (DPO)
                                    </div>
                                    <a
                                        href="mailto:privacidade@dentro.app.br"
                                        className="block mt-1 text-[#16A34A] font-semibold hover:underline"
                                    >
                                        privacidade@dentro.app.br
                                    </a>
                                    <div className="mt-1 text-sm text-[#475569]">
                                        Dentro Tecnologia Ltda. — Brasil
                                    </div>
                                </div>
                            </div>
                            <p className="mt-6">
                                Caso não fique satisfeito(a) com a nossa
                                resposta, você pode entrar em contato com a{" "}
                                <strong>
                                    Autoridade Nacional de Proteção de Dados
                                    (ANPD)
                                </strong>{" "}
                                em{" "}
                                <a
                                    href="https://www.gov.br/anpd"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    gov.br/anpd
                                </a>
                                .
                            </p>
                        </Section>
                    </article>
                </div>
            </main>

            <Footer />
        </div>
    );
}

function Section({ id, title, children }) {
    return (
        <section
            id={id}
            data-testid={`privacy-section-${id}`}
            className="scroll-mt-24"
        >
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B1C3A] tracking-tight">
                {title}
            </h2>
            <div className="mt-4 text-[16px] text-[#0B1C3A]/85 leading-relaxed space-y-4 [&_a]:text-[#16A34A] [&_a]:font-semibold [&_a:hover]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-[#0B1C3A]">
                {children}
            </div>
        </section>
    );
}
